import React, { useState } from 'react';

// Add CSS styles with keyframes
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes slideDown {
    from { transform: translateY(-20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    100% { background-position: 100% 50%; }
  }
  
  @keyframes grow {
    from { transform: scale(0.8); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
`;
document.head.appendChild(styleSheet);

const GoalSetting = () => {
  // State for goals
  const [goals, setGoals] = useState([
    {
      id: 1,
      title: "Increase Sales Revenue",
      description: "Achieve 20% growth in Q4 sales compared to Q3",
      category: "Sales",
      target: 20,
      progress: 65,
      deadline: "2023-12-31",
      status: "In Progress",
      kpi: "Revenue Growth"
    },
    {
      id: 2,
      title: "Improve Customer Satisfaction",
      description: "Achieve 95% customer satisfaction rating",
      category: "Customer Service",
      target: 95,
      progress: 78,
      deadline: "2023-11-30",
      status: "In Progress",
      kpi: "CSAT Score"
    },
    {
      id: 3,
      title: "Reduce Employee Turnover",
      description: "Decrease turnover rate by 15%",
      category: "HR",
      target: 15,
      progress: 100,
      deadline: "2023-10-15",
      status: "Completed",
      kpi: "Turnover Rate"
    },
    {
      id: 4,
      title: "Launch New Product",
      description: "Successfully launch new product line by end of Q4",
      category: "Product",
      target: 100,
      progress: 35,
      deadline: "2023-12-15",
      status: "In Progress",
      kpi: "Product Launch"
    }
  ]);

  // State for form
  const [showForm, setShowForm] = useState(false);
  const [newGoal, setNewGoal] = useState({
    title: "",
    description: "",
    category: "Sales",
    target: "",
    deadline: "",
    kpi: ""
  });

  // State for active goal
  const [activeGoal, setActiveGoal] = useState(goals[0]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewGoal({
      ...newGoal,
      [name]: value
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    const goal = {
      id: goals.length + 1,
      title: newGoal.title,
      description: newGoal.description,
      category: newGoal.category,
      target: parseInt(newGoal.target),
      progress: 0,
      deadline: newGoal.deadline,
      status: "Not Started",
      kpi: newGoal.kpi
    };
    
    setGoals([...goals, goal]);
    setNewGoal({
      title: "",
      description: "",
      category: "Sales",
      target: "",
      deadline: "",
      kpi: ""
    });
    setShowForm(false);
  };

  // Handle progress update
  const updateProgress = (id, newProgress) => {
    const updatedGoals = goals.map(goal => 
      goal.id === id 
        ? { 
            ...goal, 
            progress: Math.min(newProgress, 100),
            status: newProgress >= 100 ? "Completed" : "In Progress"
          } 
        : goal
    );
    setGoals(updatedGoals);
  };

  // Get category color
  const getCategoryColor = (category) => {
    switch(category) {
      case "Sales": return "#ff6b6b";
      case "Customer Service": return "#4ecdc4";
      case "HR": return "#45b7d1";
      case "Product": return "#f9c74f";
      case "Marketing": return "#ff9f1c";
      default: return "#95a5a6";
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch(status) {
      case "Completed": return "#2ecc71";
      case "In Progress": return "#3498db";
      case "Not Started": return "#e74c3c";
      default: return "#95a5a6";
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Goal Setting & Tracking</h2>
      
      {/* Stats Overview */}
      <div style={styles.statsContainer}>
        <div style={styles.statCard}>
          <h3 style={styles.statNumber}>{goals.length}</h3>
          <p style={styles.statLabel}>Total Goals</p>
        </div>
        <div style={styles.statCard}>
          <h3 style={styles.statNumber}>{goals.filter(g => g.status === "Completed").length}</h3>
          <p style={styles.statLabel}>Completed</p>
        </div>
        <div style={styles.statCard}>
          <h3 style={styles.statNumber}>{goals.filter(g => g.status === "In Progress").length}</h3>
          <p style={styles.statLabel}>In Progress</p>
        </div>
        <div style={styles.statCard}>
          <h3 style={styles.statNumber}>
            {Math.round(goals.reduce((sum, goal) => sum + goal.progress, 0) / goals.length)}%
          </h3>
          <p style={styles.statLabel}>Avg. Progress</p>
        </div>
      </div>

      <div style={styles.content}>
        {/* Goals List */}
        <div style={styles.goalsList}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionHeading}>Your Goals</h3>
            <button 
              onClick={() => setShowForm(!showForm)} 
              style={styles.addButton}
            >
              {showForm ? 'Cancel' : '+ New Goal'}
            </button>
          </div>

          {/* New Goal Form */}
          {showForm && (
            <div style={styles.formContainer}>
              <form onSubmit={handleSubmit} style={styles.form}>
                <h4 style={styles.formTitle}>Create New Goal</h4>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Goal Title</label>
                    <input 
                      type="text" 
                      name="title" 
                      value={newGoal.title} 
                      onChange={handleInputChange}
                      style={styles.input}
                      required
                      placeholder="Enter goal title"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Category</label>
                    <select 
                      name="category" 
                      value={newGoal.category} 
                      onChange={handleInputChange}
                      style={styles.select}
                    >
                      <option value="Sales">Sales</option>
                      <option value="Customer Service">Customer Service</option>
                      <option value="HR">HR</option>
                      <option value="Product">Product</option>
                      <option value="Marketing">Marketing</option>
                    </select>
                  </div>
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Description</label>
                  <textarea 
                    name="description" 
                    value={newGoal.description} 
                    onChange={handleInputChange}
                    style={styles.textarea}
                    placeholder="Describe your goal"
                    required
                  />
                </div>
                
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Target (%)</label>
                    <input 
                      type="number" 
                      name="target" 
                      value={newGoal.target} 
                      onChange={handleInputChange}
                      style={styles.input}
                      min="1"
                      max="100"
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>KPI</label>
                    <input 
                      type="text" 
                      name="kpi" 
                      value={newGoal.kpi} 
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Key performance indicator"
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Deadline</label>
                    <input 
                      type="date" 
                      name="deadline" 
                      value={newGoal.deadline} 
                      onChange={handleInputChange}
                      style={styles.input}
                      required
                    />
                  </div>
                </div>
                
                <button type="submit" style={styles.submitButton}>
                  Create Goal
                </button>
              </form>
            </div>
          )}

          {/* Goals Cards */}
          <div style={styles.goalsContainer}>
            {goals.map(goal => (
              <div 
                key={goal.id} 
                style={{
                  ...styles.goalCard,
                  ...(activeGoal.id === goal.id ? styles.activeGoalCard : {})
                }}
                onClick={() => setActiveGoal(goal)}
              >
                <div style={styles.goalHeader}>
                  <h4 style={styles.goalTitle}>{goal.title}</h4>
                  <span style={{
                    ...styles.statusBadge,
                    backgroundColor: getStatusColor(goal.status)
                  }}>
                    {goal.status}
                  </span>
                </div>
                
                <div style={styles.categoryContainer}>
                  <span style={{
                    ...styles.categoryBadge,
                    backgroundColor: getCategoryColor(goal.category)
                  }}>
                    {goal.category}
                  </span>
                  <span style={styles.kpiText}>KPI: {goal.kpi}</span>
                </div>
                
                <p style={styles.goalDescription}>{goal.description}</p>
                
                <div style={styles.progressContainer}>
                  <div style={styles.progressLabels}>
                    <span>Progress</span>
                    <span>{goal.progress}%</span>
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{
                      ...styles.progressFill,
                      width: `${goal.progress}%`,
                      background: `linear-gradient(90deg, ${getCategoryColor(goal.category)}, ${getCategoryColor(goal.category)}77)`
                    }}></div>
                  </div>
                </div>
                
                <div style={styles.goalFooter}>
                  <span style={styles.deadline}>Deadline: {goal.deadline}</span>
                  <div style={styles.progressControls}>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        updateProgress(goal.id, goal.progress - 10);
                      }}
                      style={styles.progressButton}
                      disabled={goal.progress <= 0}
                    >
                      -
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        updateProgress(goal.id, goal.progress + 10);
                      }}
                      style={styles.progressButton}
                      disabled={goal.progress >= 100}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Goal Details */}
        <div style={styles.goalDetails}>
          <h3 style={styles.sectionHeading}>Goal Details</h3>
          <div style={styles.detailCard}>
            <div style={styles.detailHeader}>
              <h2 style={styles.detailTitle}>{activeGoal.title}</h2>
              <span style={{
                ...styles.detailStatus,
                backgroundColor: getStatusColor(activeGoal.status)
              }}>
                {activeGoal.status}
              </span>
            </div>
            
            <div style={styles.detailCategory}>
              <span style={{
                ...styles.detailCategoryBadge,
                backgroundColor: getCategoryColor(activeGoal.category)
              }}>
                {activeGoal.category}
              </span>
              <span style={styles.detailKpi}>KPI: {activeGoal.kpi}</span>
            </div>
            
            <p style={styles.detailDescription}>{activeGoal.description}</p>
            
            <div style={styles.detailProgress}>
              <h4 style={styles.progressTitle}>Progress Overview</h4>
              <div style={styles.progressCircleContainer}>
                <div style={{
                  ...styles.progressCircle,
                  background: `conic-gradient(${getCategoryColor(activeGoal.category)} ${activeGoal.progress * 3.6}deg, #f0f0f0 0deg)`
                }}>
                  <div style={styles.progressCircleInner}>
                    <span style={styles.progressCircleText}>{activeGoal.progress}%</span>
                  </div>
                </div>
              </div>
              
              <div style={styles.progressSliderContainer}>
                <label style={styles.sliderLabel}>Update Progress: {activeGoal.progress}%</label>
                <input 
                  type="range" 
                  min="0" 
                  max="100" 
                  value={activeGoal.progress} 
                  onChange={(e) => updateProgress(activeGoal.id, parseInt(e.target.value))}
                  style={styles.progressSlider}
                />
                <div style={styles.sliderMarks}>
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>
            
            <div style={styles.detailGrid}>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Target</span>
                <span style={styles.detailValue}>{activeGoal.target}%</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Deadline</span>
                <span style={styles.detailValue}>{activeGoal.deadline}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Days Left</span>
                <span style={styles.detailValue}>
                  {Math.ceil((new Date(activeGoal.deadline) - new Date()) / (1000 * 60 * 60 * 24))}
                </span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Created On</span>
                <span style={styles.detailValue}>2023-10-01</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Inline CSS styles with gradients and animations
const styles = {
  container: {
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    padding: '20px',
    backgroundColor: '#f5f7f9',
    minHeight: '100vh',
    animation: 'fadeIn 0.5s ease-in-out'
  },
  heading: {
    color: '#2c3e50',
    marginBottom: '25px',
    fontSize: '28px',
    fontWeight: '600',
    background: 'linear-gradient(90deg, #3498db, #2c3e50)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'gradientShift 3s infinite alternate'
  },
  statsContainer: {
    display: 'flex',
    gap: '20px',
    marginBottom: '30px',
    flexWrap: 'wrap'
  },
  statCard: {
    flex: '1',
    minWidth: '180px',
    background: 'linear-gradient(135deg, #3498db, #2c3e50)',
    color: 'white',
    borderRadius: '15px',
    padding: '20px',
    textAlign: 'center',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    animation: 'slideUp 0.5s ease-out'
  },
  statNumber: {
    fontSize: '32px',
    fontWeight: '700',
    margin: '0 0 10px 0',
    textShadow: '0 2px 4px rgba(0,0,0,0.2)'
  },
  statLabel: {
    margin: '0',
    fontSize: '14px',
    fontWeight: '600',
    opacity: '0.9'
  },
  content: {
    display: 'flex',
    gap: '30px',
    alignItems: 'flex-start'
  },
  goalsList: {
    flex: '1'
  },
  goalDetails: {
    flex: '1',
    maxWidth: '500px'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  sectionHeading: {
    color: '#2c3e50',
    margin: '0',
    fontSize: '20px',
    fontWeight: '600'
  },
  addButton: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #2ecc71, #27ae60)',
    color: 'white',
    border: 'none',
    borderRadius: '30px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    transition: 'transform 0.2s, box-shadow 0.2s'
  },
  formContainer: {
    backgroundColor: 'white',
    borderRadius: '15px',
    padding: '25px',
    marginBottom: '20px',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
    animation: 'slideDown 0.3s ease-out'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  formTitle: {
    margin: '0 0 15px 0',
    color: '#2c3e50',
    fontSize: '18px'
  },
  formRow: {
    display: 'flex',
    gap: '15px'
  },
  formGroup: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    marginBottom: '8px',
    fontWeight: '600',
    color: '#34495e',
    fontSize: '14px'
  },
  input: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'border-color 0.3s, box-shadow 0.3s'
  },
  select: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: 'white'
  },
  textarea: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    minHeight: '80px',
    resize: 'vertical',
    fontFamily: 'inherit'
  },
  submitButton: {
    padding: '14px',
    background: 'linear-gradient(135deg, #3498db, #2980b9)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    marginTop: '10px'
  },
  goalsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  goalCard: {
    backgroundColor: 'white',
    borderRadius: '15px',
    padding: '20px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    animation: 'slideUp 0.5s ease-out'
  },
  activeGoalCard: {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.15)',
    borderLeft: '4px solid #3498db'
  },
  goalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px'
  },
  goalTitle: {
    margin: '0',
    color: '#2c3e50',
    fontSize: '18px',
    fontWeight: '600'
  },
  statusBadge: {
    padding: '5px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white'
  },
  categoryContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px'
  },
  categoryBadge: {
    padding: '5px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white'
  },
  kpiText: {
    fontSize: '12px',
    color: '#7f8c8d'
  },
  goalDescription: {
    margin: '0 0 15px 0',
    color: '#7f8c8d',
    fontSize: '14px',
    lineHeight: '1.5'
  },
  progressContainer: {
    marginBottom: '15px'
  },
  progressLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '14px',
    color: '#34495e'
  },
  progressBar: {
    height: '10px',
    backgroundColor: '#f0f0f0',
    borderRadius: '5px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    borderRadius: '5px',
    transition: 'width 0.5s ease'
  },
  goalFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  deadline: {
    fontSize: '12px',
    color: '#7f8c8d'
  },
  progressControls: {
    display: 'flex',
    gap: '8px'
  },
  progressButton: {
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#3498db',
    color: 'white',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  detailCard: {
    backgroundColor: 'white',
    borderRadius: '15px',
    padding: '25px',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
    animation: 'fadeIn 0.5s ease-out'
  },
  detailHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px'
  },
  detailTitle: {
    margin: '0',
    color: '#2c3e50',
    fontSize: '24px',
    fontWeight: '600'
  },
  detailStatus: {
    padding: '5px 15px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
    color: 'white'
  },
  detailCategory: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px'
  },
  detailCategoryBadge: {
    padding: '5px 15px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
    color: 'white'
  },
  detailKpi: {
    fontSize: '14px',
    color: '#7f8c8d'
  },
  detailDescription: {
    margin: '0 0 25px 0',
    color: '#7f8c8d',
    fontSize: '16px',
    lineHeight: '1.5'
  },
  detailProgress: {
    marginBottom: '25px'
  },
  progressTitle: {
    margin: '0 0 15px 0',
    color: '#2c3e50',
    fontSize: '18px'
  },
  progressCircleContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '25px'
  },
  progressCircle: {
    width: '150px',
    height: '150px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'grow 1s ease-out'
  },
  progressCircleInner: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    backgroundColor: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 0 10px rgba(0,0,0,0.1)'
  },
  progressCircleText: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#2c3e50'
  },
  progressSliderContainer: {
    marginTop: '20px'
  },
  sliderLabel: {
    display: 'block',
    marginBottom: '10px',
    fontWeight: '600',
    color: '#34495e'
  },
  progressSlider: {
    width: '100%',
    height: '8px',
    borderRadius: '4px',
    outline: 'none',
    transition: 'opacity 0.2s',
    WebkitAppearance: 'none',
    backgroundColor: '#f0f0f0'
  },
  sliderMarks: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '5px',
    fontSize: '12px',
    color: '#7f8c8d'
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '15px'
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column'
  },
  detailLabel: {
    fontSize: '12px',
    color: '#7f8c8d',
    fontWeight: '600',
    marginBottom: '5px'
  },
  detailValue: {
    fontSize: '16px',
    color: '#2c3e50',
    fontWeight: '600'
  }
};

// Add hover effects
Object.assign(styles.statCard, {
  ':hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 30px rgba(0,0,0,0.15)'
  }
});

Object.assign(styles.addButton, {
  ':hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
  }
});

Object.assign(styles.submitButton, {
  ':hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
  }
});

Object.assign(styles.goalCard, {
  ':hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
  }
});

Object.assign(styles.progressButton, {
  ':hover': {
    backgroundColor: '#2980b9'
  },
  ':disabled': {
    backgroundColor: '#bdc3c7',
    cursor: 'not-allowed'
  }
});

export default GoalSetting;
